import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import DashboardContainer from '../containers/Dashboard';

import Button from '@material-ui/core/Button';
import Collapse from '@material-ui/core/Collapse';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';

import CheckIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import NeutralIcon from '@material-ui/icons/RadioButtonUnchecked';
import RunningIcon from '@material-ui/icons/Autorenew';
import WarningIcon from '@material-ui/icons/Warning';

import DgsDialog from '../components/DgsDialog';
import ConwayDialog from '../components/ConwayDialog';

import { getProcessHealth } from '../modules/system/processHealth';

const REFRESH_MS = 60 * 1000;

const COLORS = {
	good: '#4CAF50',
	warning: '#FFC107',
	error: '#D93025',
	neutral: '#9E9E9E',
};

const styles = (theme) => {
	return {
		panel: {
			marginTop: 30,
		},
		innerPaper: {
			padding: 10,
			paddingTop: 5,
			textAlign: 'left',
			fontSize: 14,
			[theme.breakpoints.up('md')]: {
				padding: 30,
				paddingTop: 20,
			},
		},
		// banner
		//- Deliberately calm: a plain white card, no big status icon or coloured
		//- background. The per-problem icons in the list carry the severity.
		banner: {
			borderRadius: 10,
			padding: '16px 20px',
			backgroundColor: '#fff',
			border: '1px solid #e0e0e0',
			color: '#333',
			marginBottom: 10,
		},
		bannerHeadline: { fontSize: 16, fontWeight: 700, lineHeight: 1.2 },
		bannerSub: { fontSize: 12, color: '#777', marginTop: 2 },
		problemList: {
			marginTop: 12,
			paddingLeft: 0,
			listStyle: 'none',
			fontSize: 14,
		},
		problemItem: {
			padding: '4px 0',
			display: 'flex',
			alignItems: 'flex-start',
		},
		problemIcon: { width: 16, height: 16, marginRight: 8, marginTop: 2, flexShrink: 0 },
		problemSection: { opacity: 0.75, marginRight: 6 },

		// sections
		title: {
			textTransform: 'uppercase',
			color: '#777',
			fontWeight: 900,
			letterSpacing: 2,
			marginBottom: 5,
		},
		sectionHeader: {
			display: 'flex',
			alignItems: 'center',
			cursor: 'pointer',
			marginTop: 18,
			padding: '6px 0',
			borderBottom: '1px solid #e0e0e0',
		},
		sectionTitle: {
			flex: 1,
			fontWeight: 700,
			fontSize: 15,
		},
		sectionCount: {
			color: '#999',
			fontSize: 12,
			marginRight: 8,
		},
		row: {
			margin: 0,
			width: '100%',
			backgroundColor: '#fff',
			padding: '4px 0',
		},
		oddRow: {
			margin: 0,
			width: '100%',
			backgroundColor: '#f4f4f4',
			padding: '4px 0',
		},
		rowLabel: { fontWeight: 500 },
		rowSub: {
			fontSize: 12,
			fontWeight: 300,
			color: '#888',
		},
		rowSummary: {
			fontSize: 13,
			color: '#444',
		},
		rowReason: {
			fontSize: 12,
			color: COLORS.error,
		},
		rowReasonWarning: {
			fontSize: 12,
			color: '#a87900',
		},
		value: { fontWeight: 600 },
		statusIcon: {
			width: 16,
			height: 16,
			verticalAlign: 'middle',
			marginLeft: 6,
		},
		statusIconLarge: {
			width: 28,
			height: 28,
			verticalAlign: 'middle',
		},
		updateButtonContainer: {
			display: 'flex',
			justifyContent: 'flex-end',
			marginTop: 12,
		},
		updateButton: { borderRadius: 100 },
		footer: {
			marginTop: 20,
			fontSize: 11,
			color: '#aaa',
			textAlign: 'right',
		},
	};
};

const StatusIcon = ({ status, isRunning, className, title }) => {
	const style = { color: COLORS[status] || COLORS.neutral };
	let Icon = NeutralIcon;
	if (isRunning) {
		Icon = RunningIcon;
	} else if (status === 'good') {
		Icon = CheckIcon;
	} else if (status === 'warning') {
		Icon = WarningIcon;
	} else if (status === 'error') {
		Icon = ErrorIcon;
	}
	const icon = (
		<span style={style}>
			<Icon className={className} />
		</span>
	);
	if (!title) {
		return icon;
	}
	return <Tooltip title={title}>{icon}</Tooltip>;
};

StatusIcon.propTypes = {
	status: PropTypes.string,
	isRunning: PropTypes.bool,
	className: PropTypes.string,
	title: PropTypes.string,
};

class Dashboard extends Component {
	constructor(props) {
		super(props);
		this.state = {
			health: undefined,
			loadError: undefined,
			isDgsDialogOpen: false,
			isConwayDialogOpen: false,
			//- Sections with problems start open; green ones start collapsed.
			openSections: {},
			isOpisOpen: false,
			isNotifierLogOpen: false,
		};
		this.load = this.load.bind(this);
		this.toggleSection = this.toggleSection.bind(this);
		this.handleOpenDgsDialog = this.handleOpenDgsDialog.bind(this);
		this.handleCloseDgsDialog = this.handleCloseDgsDialog.bind(this);
		this.handleOpenConwayDialog = this.handleOpenConwayDialog.bind(this);
		this.handleCloseConwayDialog = this.handleCloseConwayDialog.bind(this);
	}

	componentDidMount() {
		this.load();
		this.timer = setInterval(this.load, REFRESH_MS);
	}

	componentWillUnmount() {
		clearInterval(this.timer);
	}

	load() {
		getProcessHealth({
			callback: (err, r) => {
				if (err) {
					this.setState({ loadError: _.get(err, '[0].message') || String(err) });
					return;
				}
				this.setState((state) => {
					//- First load decides which sections are open; later refreshes leave the
					//- user's toggles alone.
					let openSections = state.openSections;
					if (!state.health) {
						openSections = {};
						(r.sections || []).forEach((s) => {
							openSections[s.key] = s.status !== 'good' && s.status !== 'neutral';
						});
					}
					return { health: r, loadError: undefined, openSections };
				});
			},
		});
	}

	toggleSection(key) {
		this.setState((state) => ({
			openSections: { ...state.openSections, [key]: !state.openSections[key] },
		}));
	}

	handleOpenDgsDialog() {
		this.setState({ isDgsDialogOpen: true });
	}

	handleCloseDgsDialog() {
		this.setState({ isDgsDialogOpen: false });
		this.load();
	}

	handleOpenConwayDialog() {
		this.setState({ isConwayDialogOpen: true });
	}

	handleCloseConwayDialog() {
		this.setState({ isConwayDialogOpen: false });
		this.load();
	}

	renderBanner(health) {
		const { classes } = this.props;

		return (
			<div className={classes.banner}>
				<div>
					<div className={classes.bannerHeadline}>{health.headline}</div>
					<div className={classes.bannerSub}>
						Checked {health.generatedAtFormatted} · refreshes every minute
					</div>
					{health.problems.length > 0 && (
						<ul className={classes.problemList}>
							{health.problems.map((p) => (
								<li key={p.key} className={classes.problemItem}>
									<StatusIcon status={p.status} className={classes.problemIcon} />
									<span>
										<span className={classes.problemSection}>{p.section} ·</span>
										<strong>{p.display}</strong>
										{p.statusReason ? ` — ${p.statusReason}` : ''}
									</span>
								</li>
							))}
						</ul>
					)}
				</div>
			</div>
		);
	}

	renderProcessRow(row, index) {
		const { classes } = this.props;
		const reasonClass = row.status === 'error' ? classes.rowReason : classes.rowReasonWarning;
		return (
			<Grid
				container
				spacing={2}
				key={row.key}
				className={index % 2 ? classes.oddRow : classes.row}
				alignItems="center"
			>
				<Grid item xs={7} style={{ textAlign: 'left', lineHeight: 1.3 }}>
					<span className={classes.rowLabel}>{row.display}</span>
					<br />
					<span className={classes.rowSub}>{row.scheduleDisplay}</span>
					{row.resultSummary && <div className={classes.rowSummary}>{row.resultSummary}</div>}
					{row.statusReason && row.status !== 'good' && (
						<div className={reasonClass}>{row.statusReason}</div>
					)}
				</Grid>
				<Grid item xs={5} style={{ textAlign: 'right', lineHeight: 1.3 }}>
					<span className={classes.value}>{row.lastRunAtFormatted}</span>
					<StatusIcon
						status={row.status}
						isRunning={row.isRunning}
						className={classes.statusIcon}
						title={row.statusReason || ''}
					/>
					<br />
					<span className={classes.rowSub}>
						{row.lastRunAgeFormatted && row.lastRunAgeFormatted !== 'never'
							? `${row.lastRunAgeFormatted} ago`
							: ''}
						{row.durationFormatted ? ` · took ${row.durationFormatted}` : ''}
					</span>
				</Grid>
			</Grid>
		);
	}

	renderSection(section) {
		const { classes } = this.props;
		const isOpen = !!this.state.openSections[section.key];
		return (
			<div key={section.key}>
				<div className={classes.sectionHeader} onClick={() => this.toggleSection(section.key)}>
					<StatusIcon status={section.status} className={classes.statusIcon} />
					<span className={classes.sectionTitle} style={{ marginLeft: 8 }}>
						{section.title}
					</span>
					<span className={classes.sectionCount}>{section.rowCount} tracked</span>
					<IconButton size="small">{isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
				</div>
				<Collapse in={isOpen}>
					<div>{section.rows.map((row, i) => this.renderProcessRow(row, i))}</div>
				</Collapse>
			</div>
		);
	}

	renderOpis(opis) {
		const { classes } = this.props;
		const { isOpisOpen } = this.state;
		return (
			<Paper className={classes.panel} style={{ marginTop: 0 }}>
				<div className={classes.innerPaper}>
					<Typography variant="h5" align="left" gutterBottom style={{ position: 'relative' }}>
						OPIS
						<span style={{ position: 'absolute', right: 0 }}>
							<StatusIcon status={opis.status} className={classes.statusIconLarge} />
						</span>
					</Typography>
					<div
						className={classes.sectionHeader}
						style={{ marginTop: 0 }}
						onClick={() => this.setState({ isOpisOpen: !isOpisOpen })}
					>
						<span className={classes.sectionTitle}>{opis.summaryLine}</span>
						<IconButton size="small">
							{isOpisOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
						</IconButton>
					</div>
					<Collapse in={isOpisOpen}>
						<div>
							{opis.files.map((file, i) => (
								<Grid
									container
									spacing={2}
									key={file.key}
									className={i % 2 ? classes.oddRow : classes.row}
								>
									<Grid item xs={8} style={{ textAlign: 'left' }}>
										<span className={classes.rowLabel}>{file.display}</span>
										{file.statusReason && (
											<div className={classes.rowReasonWarning}>{file.statusReason}</div>
										)}
									</Grid>
									<Grid item xs={4} style={{ textAlign: 'right' }}>
										<span className={classes.value}>{file.dateDownloadedFormatted}</span>
										<StatusIcon status={file.status} className={classes.statusIcon} />
									</Grid>
								</Grid>
							))}
						</div>
					</Collapse>
				</div>
			</Paper>
		);
	}

	renderLatestData(latestData) {
		const { classes } = this.props;
		return (
			<Paper className={classes.panel}>
				<div className={classes.innerPaper}>
					<Typography variant="h5" align="left" gutterBottom>
						Latest Data
					</Typography>
					<div>
						{latestData.map((d, i) => (
							<Grid
								container
								spacing={2}
								key={d.key}
								className={i % 2 ? classes.oddRow : classes.row}
							>
								<Grid item xs={8} style={{ textAlign: 'left', lineHeight: 1.3 }}>
									<span className={classes.rowLabel}>{d.display}</span>
									<br />
									<span className={classes.rowSub}>{d.dateCreatedFormatted}</span>
								</Grid>
								<Grid item xs={4} style={{ textAlign: 'right' }}>
									<span className={classes.value}>{d.value}</span>
								</Grid>
							</Grid>
						))}
					</div>
				</div>
			</Paper>
		);
	}

	renderManualInput({ input, title, buttonLabel, onOpen, canMakeUpdates }) {
		const { classes } = this.props;
		const rows = [
			[input.display, input.value],
			['Last updated', input.lastUpdatedFormatted],
			['Last updated by', input.lastUpdatedBy],
		];
		return (
			<Paper className={classes.panel}>
				<div className={classes.innerPaper}>
					<Typography variant="h5" align="left" gutterBottom style={{ position: 'relative' }}>
						{title}
						<span style={{ position: 'absolute', right: 0 }}>
							<StatusIcon
								status={input.status}
								className={classes.statusIconLarge}
								title={input.statusReason || ''}
							/>
						</span>
					</Typography>
					{input.statusReason && (
						<div className={classes.rowReasonWarning} style={{ marginBottom: 6 }}>
							{input.statusReason}
						</div>
					)}
					{rows.map(([label, value], i) => (
						<Grid
							container
							spacing={2}
							key={label}
							className={i % 2 ? classes.oddRow : classes.row}
						>
							<Grid item xs={8} style={{ textAlign: 'left' }}>
								<span className={classes.rowLabel}>{label}</span>
							</Grid>
							<Grid item xs={4} style={{ textAlign: 'right' }}>
								<span className={classes.value}>{value}</span>
							</Grid>
						</Grid>
					))}
					{canMakeUpdates && (
						<div className={classes.updateButtonContainer}>
							<Button color="primary" className={classes.updateButton} onClick={onOpen}>
								{buttonLabel}
							</Button>
						</div>
					)}
				</div>
			</Paper>
		);
	}

	renderNotifierLogs(logs) {
		const { classes } = this.props;
		const { isNotifierLogOpen } = this.state;
		if (_.isEmpty(logs)) {
			return null;
		}
		return (
			<div>
				<div
					className={classes.sectionHeader}
					onClick={() => this.setState({ isNotifierLogOpen: !isNotifierLogOpen })}
				>
					<StatusIcon status={logs[0].status} className={classes.statusIcon} />
					<span className={classes.sectionTitle} style={{ marginLeft: 8 }}>
						Recent notifier batches
					</span>
					<span className={classes.sectionCount}>{logs.length} shown</span>
					<IconButton size="small">
						{isNotifierLogOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
					</IconButton>
				</div>
				<Collapse in={isNotifierLogOpen}>
					<div>
						{logs.map((log, i) => (
							<Grid
								container
								spacing={2}
								key={log.notifierLogId}
								className={i % 2 ? classes.oddRow : classes.row}
							>
								<Grid item xs={5} style={{ textAlign: 'left' }}>
									{log.dateCreatedFormatted}
								</Grid>
								<Grid item xs={3} style={{ textAlign: 'left' }}>
									{log.jobCount} jobs
								</Grid>
								<Grid item xs={4} style={{ textAlign: 'right' }}>
									{log.statusDisplay}
									<StatusIcon
										status={log.status}
										className={classes.statusIcon}
										title={log.errorMessage || ''}
									/>
								</Grid>
							</Grid>
						))}
					</div>
				</Collapse>
			</div>
		);
	}

	render() {
		const { classes, authUser } = this.props;
		const { health, loadError } = this.state;

		if (loadError && !health) {
			return (
				<Paper className={classes.panel}>
					<div className={classes.innerPaper}>Could not load dashboard health: {loadError}</div>
				</Paper>
			);
		}
		if (!health || !authUser) {
			return null;
		}

		const canMakeUpdates = _.get(authUser, ['permissions', 'canMakeUpdates']);

		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					{this.renderBanner(health)}
					{loadError && (
						<div className={classes.rowReasonWarning}>
							Last refresh failed ({loadError}); showing the previous result.
						</div>
					)}
				</Grid>

				<Grid item xs={12} md={7}>
					<Paper style={{ marginTop: 0 }}>
						<div className={classes.innerPaper}>
							<Typography variant="h5" align="left" gutterBottom style={{ position: 'relative' }}>
								Process Health
								<span style={{ position: 'absolute', right: 0 }}>
									<StatusIcon status={health.overallStatus} className={classes.statusIconLarge} />
								</span>
							</Typography>
							{health.sections.map((section) => this.renderSection(section))}
							{this.renderNotifierLogs(health.notifierLogs)}
							<div className={classes.footer}>
								Statuses are decided on the server from each process&apos;s own schedule.
							</div>
						</div>
					</Paper>
				</Grid>

				<Grid item xs={12} md={5}>
					{this.renderOpis(health.opis)}
					{this.renderLatestData(health.latestData)}
					{this.renderManualInput({
						input: health.manualInputs.dgs,
						title: 'DGS data',
						buttonLabel: 'Update DGS data',
						onOpen: this.handleOpenDgsDialog,
						canMakeUpdates,
					})}
					{this.renderManualInput({
						input: health.manualInputs.conway,
						title: 'Conway Natural Gas Pricing',
						buttonLabel: 'Update Conway pricing',
						onOpen: this.handleOpenConwayDialog,
						canMakeUpdates,
					})}
				</Grid>

				<DgsDialog
					onClose={this.handleCloseDgsDialog}
					addDataRecord={this.props.addDataRecord}
					currentDgsValue={health.manualInputs.dgs.value}
					open={this.state.isDgsDialogOpen}
				/>

				<ConwayDialog
					onClose={this.handleCloseConwayDialog}
					addDataRecord={this.props.addDataRecord}
					currentConwayValue={health.manualInputs.conway.value}
					open={this.state.isConwayDialogOpen}
				/>
			</Grid>
		);
	}
}

Dashboard.propTypes = {
	classes: PropTypes.object.isRequired,
	addDataRecord: PropTypes.func.isRequired,
	authUser: PropTypes.object,
};

export default _.flow([withStyles(styles), DashboardContainer])(Dashboard);
